import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  Button, 
  Chip, 
  LinearProgress, 
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Divider,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormLabel
} from '@mui/material';

import { 
  HowToVote as VoteIcon, 
  Add as AddIcon, 
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { getApiUrl, API_ENDPOINTS } from '../config/api';

interface Proposal {
  id: string;
  title: string;
  description: string;
  type: 'policy' | 'project' | 'reward' | 'other';
  status: 'draft' | 'active' | 'passed' | 'rejected' | 'expired';
  startDate: string | null;
  endDate: string | null;
  creatorId: string;
  requiredVotes: number | null;
  createdAt: string;
  updatedAt: string;
  creator: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  votes: Array<{
    id: string;
    vote: 'yes' | 'no' | 'abstain';
    reason: string | null;
    userId: string;
    user: {
      id: string;
      firstName: string;
      lastName: string;
    };
  }>;
  voteStats: {
    yes: number;
    no: number;
    abstain: number;
    total: number;
    approvalRate: number;
  };
}

interface Vote {
  id: string;
  vote: 'yes' | 'no' | 'abstain';
  reason: string | null;
  userId: string;
  user: {
    id: string;
    firstName: string;
    lastName: string;
  };
}

const Governance: React.FC = () => {
  const { user, token, hasPermission } = useAuth();
  const { t } = useTranslation();
  const [proposals, setProposals] = useState<Proposal[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [voteDialogOpen, setVoteDialogOpen] = useState(false);
  const [selectedProposal, setSelectedProposal] = useState<Proposal | null>(null);
  const [userVote, setUserVote] = useState<Vote | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Form states
  const [proposalForm, setProposalForm] = useState({
    title: '',
    description: '',
    type: 'other' as const,
    startDate: '',
    endDate: '',
    requiredVotes: ''
  });

  const [voteForm, setVoteForm] = useState({
    vote: 'yes' as 'yes' | 'no' | 'abstain',
    reason: ''
  });

  useEffect(() => {
    fetchProposals();
  }, []);

  const fetchProposals = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(getApiUrl(API_ENDPOINTS.PROPOSALS), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch proposals');
      }

      const data = await response.json();
      setProposals(data.proposals);
    } catch (err) {
      console.error('Error fetching proposals:', err);
      setError('Failed to load proposals');
    } finally {
      setLoading(false);
    }
  };

  const fetchUserVote = async (proposalId: string) => {
    try {
      const response = await fetch(getApiUrl(API_ENDPOINTS.MY_VOTE(proposalId)), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setUserVote(data.vote);
      }
    } catch (err) {
      console.error('Error fetching user vote:', err);
    }
  };

  const handleCreateProposal = async () => {
    try {
      setSubmitting(true);

      // Prepare the request data, filtering out empty strings
      const requestData: any = {
        title: proposalForm.title,
        description: proposalForm.description,
        type: proposalForm.type
      };

      // Only include optional fields if they have valid values
      if (proposalForm.startDate && proposalForm.startDate.trim() !== '') {
        requestData.startDate = proposalForm.startDate;
      }

      if (proposalForm.endDate && proposalForm.endDate.trim() !== '') {
        requestData.endDate = proposalForm.endDate;
      }

      if (proposalForm.requiredVotes && proposalForm.requiredVotes.trim() !== '') {
        requestData.requiredVotes = parseInt(proposalForm.requiredVotes);
      }

      const response = await fetch(getApiUrl(API_ENDPOINTS.PROPOSALS), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        if (errorData.errors && Array.isArray(errorData.errors)) {
          const errorMessages = errorData.errors.map((err: any) => `${err.path}: ${err.msg}`).join(', ');
          throw new Error(`Validation errors: ${errorMessages}`);
        }
        throw new Error(errorData.error || 'Failed to create proposal');
      }

      await fetchProposals();
      setCreateDialogOpen(false);
      setProposalForm({
        title: '',
        description: '',
        type: 'other',
        startDate: '',
        endDate: '',
        requiredVotes: ''
      });
    } catch (err) {
      console.error('Error creating proposal:', err);
      setError(err instanceof Error ? err.message : 'Failed to create proposal');
    } finally {
      setSubmitting(false);
    }
  };

  const handleVote = async () => {
    if (!selectedProposal) return;

    try {
      setSubmitting(true);

      const url = getApiUrl(API_ENDPOINTS.PROPOSAL_VOTE(selectedProposal.id));

      const method = userVote ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          vote: voteForm.vote,
          reason: voteForm.reason || null
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to submit vote');
      }

      await fetchProposals();
      setVoteDialogOpen(false);
      setVoteForm({ vote: 'yes', reason: '' });
      setUserVote(null);
      setSelectedProposal(null);
    } catch (err) {
      console.error('Error submitting vote:', err);
      setError(err instanceof Error ? err.message : 'Failed to submit vote');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateStatus = async (proposalId: string, status: string) => {
    try {
      const response = await fetch(getApiUrl(API_ENDPOINTS.PROPOSAL_STATUS(proposalId)), {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status })
      });

      if (!response.ok) {
        throw new Error('Failed to update proposal status');
      }

      await fetchProposals();
    } catch (err) {
      console.error('Error updating proposal status:', err);
      setError('Failed to update proposal status');
    }
  };

  const handleDeleteProposal = async (proposalId: string) => {
    if (!window.confirm('Are you sure you want to delete this proposal?')) return;

    try {
      const response = await fetch(getApiUrl(API_ENDPOINTS.PROPOSAL_DETAILS(proposalId)), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to delete proposal');
      }

      await fetchProposals();
    } catch (err) {
      console.error('Error deleting proposal:', err);
      setError('Failed to delete proposal');
    }
  };

  const openVoteDialog = async (proposal: Proposal) => {
    setSelectedProposal(proposal);
    await fetchUserVote(proposal.id);
    if (userVote) {
      setVoteForm({
        vote: userVote.vote,
        reason: userVote.reason || ''
      });
    }
    setVoteDialogOpen(true);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'success';
      case 'passed': return 'primary';
      case 'rejected': return 'error';
      case 'expired': return 'warning';
      case 'draft': return 'default';
      default: return 'default';
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'policy': return 'primary';
      case 'project': return 'secondary';
      case 'reward': return 'success';
      case 'other': return 'default';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          {t('governance.title')}
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateDialogOpen(true)}
          sx={{ borderRadius: 2 }}
        >
          {t('governance.createProposal')}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {proposals.length === 0 ? (
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
                {t('governance.noProposals')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('governance.createFirstProposal')}
              </Typography>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* 1. TOP: Proposals that need to be Activated (Draft Status) */}
            {proposals.filter(p => p.status === 'draft' && (hasPermission('governance.edit') || p.creatorId === user?.id)).length > 0 && (
              <Box>
                <Typography variant="h5" component="h2" sx={{ mb: 2, color: 'warning.main', fontWeight: 'bold' }}>
                  ⏳ Proposals Awaiting Activation
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  These proposals are ready to be activated and put up for voting
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {proposals
                    .filter(proposal => proposal.status === 'draft' && (hasPermission('governance.edit') || proposal.creatorId === user?.id))
                    .map((proposal) => {
            const canManage = proposal.creatorId === user?.id || hasPermission('governance.edit');

            return (
                        <Card key={proposal.id} sx={{ border: '2px solid', borderColor: 'warning.main', backgroundColor: 'warning.light' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box sx={{ flex: 1 }}>
                                <Typography variant="h6" component="h2" sx={{ fontWeight: 'bold', mb: 1, color: 'warning.dark' }}>
                        {proposal.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {proposal.description}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                        <Chip
                          label={proposal.type}
                          color={getTypeColor(proposal.type) as any}
                          size="small"
                          variant="outlined"
                        />
                        <Chip
                                    label="DRAFT"
                                    color="warning"
                          size="small"
                                    sx={{ fontWeight: 'bold' }}
                        />
                        <Chip
                          label={`By ${proposal.creator.firstName} ${proposal.creator.lastName}`}
                          size="small"
                          variant="outlined"
                        />
                      </Box>
                    </Box>
                    
                    {canManage && (
                      <Box sx={{ display: 'flex', gap: 1 }}>
                          <Tooltip title="Activate Proposal">
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => handleUpdateStatus(proposal.id, 'active')}
                            >
                              <CheckCircleIcon />
                            </IconButton>
                          </Tooltip>
                                  <Tooltip title="Delete Proposal">
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={() => handleDeleteProposal(proposal.id)}
                                    >
                                      <DeleteIcon />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              )}
                            </Box>

                            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                              {t('governance.created')}: {new Date(proposal.createdAt).toLocaleDateString()}
                              {proposal.endDate && ` • ${t('governance.ends')}: ${new Date(proposal.endDate).toLocaleDateString()}`}
                            </Typography>
                          </CardContent>
                        </Card>
                      );
                    })}
                </Box>
              </Box>
            )}

            {/* 2. MIDDLE: Active Proposals (Need to be Voted) */}
            {proposals.filter(p => p.status === 'active').length > 0 && (
              <Box>
                <Typography variant="h5" component="h2" sx={{ mb: 2, color: 'primary.main', fontWeight: 'bold' }}>
                  🗳️ {t('governance.activeProposalsVoteNow')}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {t('governance.activeProposalsDescription')}
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {proposals
                    .filter(proposal => proposal.status === 'active')
                    .map((proposal) => {
                      const canVote = proposal.status === 'active';
                      const canManage = proposal.creatorId === user?.id || hasPermission('governance.edit');
                      const hasVoted = proposal.votes.some(v => v.userId === user?.id);

                      return (
                        <Card key={proposal.id} sx={{ border: '2px solid', borderColor: 'primary.main' }}>
                          <CardContent>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="h6" component="h2" sx={{ fontWeight: 'bold', mb: 1, color: 'primary.main' }}>
                                  {proposal.title}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                  {proposal.description}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                                  <Chip
                                    label={proposal.type}
                                    color={getTypeColor(proposal.type) as any}
                                    size="small"
                                    variant="outlined"
                                  />
                                  <Chip
                                    label="ACTIVE"
                                    color="primary"
                                    size="small"
                                    sx={{ fontWeight: 'bold' }}
                                  />
                                  <Chip
                                    label={`By ${proposal.creator.firstName} ${proposal.creator.lastName}`}
                                    size="small"
                                    variant="outlined"
                                  />
                                </Box>
                              </Box>
                              
                              {canManage && (
                                <Box sx={{ display: 'flex', gap: 1 }}>
                            <Tooltip title="Mark as Passed">
                              <IconButton
                                size="small"
                                      color="success"
                                onClick={() => handleUpdateStatus(proposal.id, 'passed')}
                              >
                                <CheckCircleIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Mark as Rejected">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleUpdateStatus(proposal.id, 'rejected')}
                              >
                                <CancelIcon />
                              </IconButton>
                            </Tooltip>
                        <Tooltip title="Delete Proposal">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteProposal(proposal.id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    )}
                  </Box>

                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          {t('governance.approval')} %
                        </Typography>
                        <Typography variant="body2" color="primary" sx={{ fontWeight: 'bold' }}>
                          {proposal.voteStats.approvalRate}%
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={proposal.voteStats.approvalRate}
                        sx={{ height: 8, borderRadius: 4 }}
                      />
                    </Box>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', gap: 3 }}>
                      <Typography variant="body2" color="text.secondary">
                        <strong>{proposal.voteStats.yes}</strong> {t('governance.for')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        <strong>{proposal.voteStats.no}</strong> {t('governance.against')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        <strong>{proposal.voteStats.abstain}</strong> {t('governance.abstain')}
                      </Typography>
                    </Box>
                    
                    {canVote && !hasVoted && (
                      <Button
                                  variant="contained"
                        startIcon={<VoteIcon />}
                        size="small"
                        onClick={() => openVoteDialog(proposal)}
                                  sx={{ fontWeight: 'bold' }}
                      >
                                  {t('governance.voteNow')}
                      </Button>
                    )}
                    
                    {canVote && hasVoted && (
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <Chip
                          label="Voted"
                          color="success"
                          size="small"
                          variant="outlined"
                        />
                        <Button
                          variant="text"
                          size="small"
                          onClick={() => openVoteDialog(proposal)}
                          sx={{ fontSize: '0.75rem' }}
                        >
                          Change Vote
                        </Button>
                      </Box>
                    )}
                  </Box>

                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    {t('governance.created')}: {new Date(proposal.createdAt).toLocaleDateString()}
                    {proposal.endDate && ` • ${t('governance.ends')}: ${new Date(proposal.endDate).toLocaleDateString()}`}
                  </Typography>
                </CardContent>
              </Card>
            );
                    })}
                </Box>
              </Box>
            )}

            {/* 3. BOTTOM: Passed Proposals */}
            {proposals.filter(p => p.status === 'passed').length > 0 && (
              <Box>
                <Typography variant="h5" component="h2" sx={{ mb: 2, color: 'success.main', fontWeight: 'bold' }}>
                  ✅ {t('governance.passedProposals')}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {t('governance.passedProposalsDescription')}
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {proposals
                    .filter(proposal => proposal.status === 'passed')
                    .map((proposal) => {
                      const canManage = proposal.creatorId === user?.id || hasPermission('governance.edit');

                      return (
                        <Card 
                          key={proposal.id} 
                          sx={{ 
                            border: '2px solid',
                            borderColor: 'success.main',
                            backgroundColor: 'success.light',
                            '&:hover': {
                              backgroundColor: 'success.light',
                              boxShadow: 3
                            }
                          }}
                        >
                          <CardContent>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="h6" component="h2" sx={{ fontWeight: 'bold', mb: 1, color: 'success.dark' }}>
                                  {proposal.title}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                  {proposal.description}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                                  <Chip
                                    label={proposal.type}
                                    color={getTypeColor(proposal.type) as any}
                                    size="small"
                                    variant="outlined"
                                  />
                                  <Chip
                                    label="PASSED"
                                    color="success"
                                    size="small"
                                    sx={{ fontWeight: 'bold' }}
                                  />
                                  <Chip
                                    label={`By ${proposal.creator.firstName} ${proposal.creator.lastName}`}
                                    size="small"
                                    variant="outlined"
                                  />
                                </Box>
                              </Box>
                              
                              {canManage && (
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                  <Tooltip title="Delete Proposal">
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={() => handleDeleteProposal(proposal.id)}
                                    >
                                      <DeleteIcon />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              )}
                            </Box>

                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Box sx={{ display: 'flex', gap: 3 }}>
                                <Typography variant="body2" color="text.secondary">
                                  <strong>{proposal.voteStats.yes}</strong> {t('governance.for')}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  <strong>{proposal.voteStats.no}</strong> {t('governance.against')}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  <strong>{proposal.voteStats.abstain}</strong> {t('governance.abstain')}
                                </Typography>
                              </Box>
                              
                              <Chip
                                label={`${proposal.voteStats.approvalRate}% ${t('governance.approval')}`}
                                color="success"
                                size="small"
                                sx={{ fontWeight: 'bold' }}
                              />
                            </Box>

                            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                              {t('governance.created')}: {new Date(proposal.createdAt).toLocaleDateString()}
                              {proposal.endDate && ` • ${t('governance.ends')}: ${new Date(proposal.endDate).toLocaleDateString()}`}
                            </Typography>
                          </CardContent>
                        </Card>
                      );
                    })}
                </Box>
              </Box>
            )}

            {/* Additional: Other Status Proposals (Rejected, Expired) - Only visible to admin and creator */}
            {proposals.filter(p => ['rejected', 'expired'].includes(p.status) && (hasPermission('governance.edit') || p.creatorId === user?.id)).length > 0 && (
              <Box>
                <Typography variant="h5" component="h2" sx={{ mb: 2, color: 'text.secondary', fontWeight: 'bold' }}>
                  📄 Inactive Proposals
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  These proposals have been rejected or expired
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {proposals
                    .filter(proposal => ['rejected', 'expired'].includes(proposal.status) && (hasPermission('governance.edit') || proposal.creatorId === user?.id))
                    .map((proposal) => {
                      const canManage = proposal.creatorId === user?.id || hasPermission('governance.edit');

                      return (
                        <Card key={proposal.id} sx={{ opacity: 0.7 }}>
                          <CardContent>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="h6" component="h2" sx={{ fontWeight: 'bold', mb: 1 }}>
                                  {proposal.title}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                  {proposal.description}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                                  <Chip
                                    label={proposal.type}
                                    color={getTypeColor(proposal.type) as any}
                                    size="small"
                                    variant="outlined"
                                  />
                                  <Chip
                                    label={proposal.status.toUpperCase()}
                                    color={getStatusColor(proposal.status) as any}
                                    size="small"
                                  />
                                  <Chip
                                    label={`By ${proposal.creator.firstName} ${proposal.creator.lastName}`}
                                    size="small"
                                    variant="outlined"
                                  />
                                </Box>
                              </Box>
                              
                              {canManage && (
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                  <Tooltip title="Delete Proposal">
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={() => handleDeleteProposal(proposal.id)}
                                    >
                                      <DeleteIcon />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              )}
                            </Box>

                            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                              {t('governance.created')}: {new Date(proposal.createdAt).toLocaleDateString()}
                              {proposal.endDate && ` • ${t('governance.ends')}: ${new Date(proposal.endDate).toLocaleDateString()}`}
                            </Typography>
                          </CardContent>
                        </Card>
                      );
                    })}
                </Box>
              </Box>
            )}
          </>
        )}
      </Box>

      {/* Create Proposal Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Create New Proposal</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label="Title"
              value={proposalForm.title}
              onChange={(e) => setProposalForm({ ...proposalForm, title: e.target.value })}
              required
            />
            <TextField
              fullWidth
              label="Description"
              multiline
              rows={4}
              value={proposalForm.description}
              onChange={(e) => setProposalForm({ ...proposalForm, description: e.target.value })}
              required
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select
                  value={proposalForm.type}
                  onChange={(e) => setProposalForm({ ...proposalForm, type: e.target.value as any })}
                  label="Type"
                >
                  <MenuItem value="policy">Policy</MenuItem>
                  <MenuItem value="project">Project</MenuItem>
                  <MenuItem value="reward">Reward</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              </FormControl>
              <TextField
                fullWidth
                label="Required Votes (optional)"
                type="number"
                value={proposalForm.requiredVotes}
                onChange={(e) => setProposalForm({ ...proposalForm, requiredVotes: e.target.value })}
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                label="Start Date (optional)"
                type="datetime-local"
                value={proposalForm.startDate}
                onChange={(e) => setProposalForm({ ...proposalForm, startDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                fullWidth
                label="End Date (optional)"
                type="datetime-local"
                value={proposalForm.endDate}
                onChange={(e) => setProposalForm({ ...proposalForm, endDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleCreateProposal} 
            variant="contained" 
            disabled={submitting || !proposalForm.title || !proposalForm.description}
          >
            {submitting ? <CircularProgress size={20} /> : 'Create Proposal'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Vote Dialog */}
      <Dialog open={voteDialogOpen} onClose={() => setVoteDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {userVote ? t('governance.updateYourVote') : t('governance.castYourVote')}
          {selectedProposal && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {selectedProposal.title}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <FormControl component="fieldset" sx={{ width: '100%', mb: 2 }}>
            <FormLabel component="legend">{t('governance.yourVote')}</FormLabel>
            <RadioGroup
              value={voteForm.vote}
              onChange={(e) => setVoteForm({ ...voteForm, vote: e.target.value as any })}
            >
              <FormControlLabel value="yes" control={<Radio />} label={t('governance.yes')} />
              <FormControlLabel value="no" control={<Radio />} label={t('governance.no')} />
              <FormControlLabel value="abstain" control={<Radio />} label={t('governance.abstain')} />
            </RadioGroup>
          </FormControl>
          <TextField
            fullWidth
            label={t('governance.reasonOptional')}
            multiline
            rows={3}
            value={voteForm.reason}
            onChange={(e) => setVoteForm({ ...voteForm, reason: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVoteDialogOpen(false)}>{t('common.cancel')}</Button>
          <Button 
            onClick={handleVote} 
            variant="contained" 
            disabled={submitting}
          >
            {submitting ? <CircularProgress size={20} /> : (userVote ? t('governance.updateVote') : t('governance.submitVote'))}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Governance; 